import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download,
  Lock,
  RefreshCw,
  Save,
  Search,
  Send,
  Star,
  XCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = "mocktpAdminAuth";

const STATUSES = ["New", "Reviewing", "Approved", "Invited", "Active Tester", "Rejected"];
const PLATFORMS = [
  { value: "android", label: "Android" },
  { value: "ios", label: "iPhone (iOS)" },
  { value: "both", label: "Both" },
];
const INVITATION_PLATFORMS = [
  { value: "android", label: "Android" },
  { value: "ios", label: "iPhone (iOS)" },
];

const emptyFilters = {
  search: "",
  country: "all",
  platform: "all",
  status: "all",
  favouritesOnly: false,
};

const platformLabel = (value) =>
  PLATFORMS.find((platform) => platform.value === value)?.label || "Not set";

const invitationPlatformLabel = (value) =>
  INVITATION_PLATFORMS.find((platform) => platform.value === value)?.label || "Not set";

const formatDateTime = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const toDatetimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const csvEscape = (value) => {
  const safe = value === null || value === undefined ? "" : String(value);
  return `"${safe.replace(/"/g, '""')}"`;
};

const buildCsv = (applications) => {
  const headers = [
    "Submission Date",
    "Name",
    "Email",
    "Country",
    "ADI Number",
    "Driving School",
    "Preferred Platform",
    "Status",
    "Favourite",
    "Invitation Sent Date",
    "Invitation Platform",
    "Invitation Email",
    "Admin Notes",
  ];

  const rows = applications.map((application) => [
    formatDateTime(application.created_at || application.createdAt),
    application.name,
    application.email,
    application.country,
    application.adi_number,
    application.school,
    platformLabel(application.preferredPlatform),
    application.status || "New",
    application.isFavourite ? "Yes" : "No",
    formatDateTime(application.invitationSentAt),
    invitationPlatformLabel(application.invitationPlatform),
    application.invitationEmail,
    application.adminNotes,
  ]);

  return [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
};

const normaliseApplication = (application) => ({
  ...application,
  status: application.status || "New",
  isFavourite: Boolean(application.isFavourite),
  adminNotes: application.adminNotes || "",
  preferredPlatform: application.preferredPlatform || "",
  invitationPlatform: application.invitationPlatform || "",
  invitationEmail: application.invitationEmail || "",
  applicantType: application.applicantType || "Public Applicant",
  invitationCode: application.invitationCode || "",
  invitationCodeValid: Boolean(application.invitationCodeValid),
  invitationCodeUsed: Boolean(application.invitationCodeUsed),
  invitationCodeUsedBy: application.invitationCodeUsedBy || "",
  invitationCodeUsedDate: application.invitationCodeUsedDate || "",
});

export default function AdminBetaApplications() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem(AUTH_KEY) || "");
  const [applications, setApplications] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [invitationDraft, setInvitationDraft] = useState({});
  const [invitationPlatformDraft, setInvitationPlatformDraft] = useState({});
  const [invitationEmailDraft, setInvitationEmailDraft] = useState({});

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Basic ${authToken}` } : {}),
    [authToken]
  );

  const loadApplications = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/beta-signup`, { headers: authHeaders });
      const normalised = data.map(normaliseApplication);
      setApplications(normalised);
      setNotesDraft(
        Object.fromEntries(normalised.map((application) => [application.id, application.adminNotes || ""]))
      );
      setInvitationDraft(
        Object.fromEntries(
          normalised.map((application) => [application.id, toDatetimeLocal(application.invitationSentAt)])
        )
      );
      setInvitationPlatformDraft(
        Object.fromEntries(normalised.map((application) => [application.id, application.invitationPlatform || ""]))
      );
      setInvitationEmailDraft(
        Object.fromEntries(normalised.map((application) => [application.id, application.invitationEmail || ""]))
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(AUTH_KEY);
        setAuthToken("");
        toast.error("Admin login required.");
      } else if (err?.response?.status === 503) {
        toast.error("Admin access is not configured on the backend.");
      } else {
        toast.error("Unable to load beta applications.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const login = (event) => {
    event.preventDefault();
    if (!credentials.username.trim() || !credentials.password) {
      toast.error("Enter the admin username and password.");
      return;
    }
    const token = btoa(`${credentials.username.trim()}:${credentials.password}`);
    sessionStorage.setItem(AUTH_KEY, token);
    setAuthToken(token);
    setCredentials({ username: "", password: "" });
  };

  const logout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthToken("");
    setApplications([]);
  };

  const updateApplication = async (id, payload, successMessage) => {
    setSavingId(id);
    try {
      const { data } = await axios.patch(`${API}/beta-signup/${id}`, payload, {
        headers: authHeaders,
      });
      const updated = normaliseApplication(data);
      setApplications((current) => current.map((item) => (item.id === id ? updated : item)));
      setNotesDraft((current) => ({ ...current, [id]: updated.adminNotes || "" }));
      setInvitationDraft((current) => ({
        ...current,
        [id]: toDatetimeLocal(updated.invitationSentAt),
      }));
      setInvitationPlatformDraft((current) => ({
        ...current,
        [id]: updated.invitationPlatform || "",
      }));
      setInvitationEmailDraft((current) => ({
        ...current,
        [id]: updated.invitationEmail || "",
      }));
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Admin login required." : "Update failed.");
    } finally {
      setSavingId(null);
    }
  };

  const countries = useMemo(
    () => Array.from(new Set(applications.map((item) => item.country).filter(Boolean))).sort(),
    [applications]
  );

  const summary = useMemo(() => {
    const statusCounts = Object.fromEntries(STATUSES.map((status) => [status, 0]));
    const platformCounts = Object.fromEntries(PLATFORMS.map((platform) => [platform.value, 0]));

    applications.forEach((application) => {
      statusCounts[application.status || "New"] = (statusCounts[application.status || "New"] || 0) + 1;
      if (application.preferredPlatform) {
        platformCounts[application.preferredPlatform] =
          (platformCounts[application.preferredPlatform] || 0) + 1;
      }
    });

    return { statusCounts, platformCounts };
  }, [applications]);

  const filteredApplications = useMemo(() => {
    const query = filters.search.trim().toLowerCase();

    return applications
      .filter((application) => {
        const haystack = [
          application.name,
          application.email,
          application.adi_number,
          application.school,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (query && !haystack.includes(query)) return false;
        if (filters.country !== "all" && application.country !== filters.country) return false;
        if (filters.platform !== "all" && application.preferredPlatform !== filters.platform) return false;
        if (filters.status !== "all" && (application.status || "New") !== filters.status) return false;
        if (filters.favouritesOnly && !application.isFavourite) return false;
        return true;
      })
      .sort((a, b) => {
        const aTime = new Date(a.created_at || a.createdAt || 0).getTime() || 0;
        const bTime = new Date(b.created_at || b.createdAt || 0).getTime() || 0;
        return bTime - aTime;
      });
  }, [applications, filters]);

  const exportCsv = () => {
    const csv = buildCsv(filteredApplications);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `mocktp-beta-applications-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!authToken) {
    return (
      <main className="min-h-screen bg-[#080C16] text-white flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-[#00DC82] text-[#080C16] flex items-center justify-center">
              <Lock size={22} />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Beta Admin</h1>
              <p className="text-sm text-slate-400">MockTP internal dashboard</p>
            </div>
          </div>
          <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-username">
            Username
          </label>
          <input
            id="admin-username"
            value={credentials.username}
            onChange={(event) => setCredentials((current) => ({ ...current, username: event.target.value }))}
            className="mb-4 h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm outline-none focus:border-[#00DC82]"
          />
          <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            value={credentials.password}
            onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
            className="mb-6 h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm outline-none focus:border-[#00DC82]"
          />
          <button className="h-11 w-full rounded-full bg-[#00DC82] text-[#080C16] font-semibold hover:bg-[#00B86B] transition-colors">
            Sign in
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#080C16] text-white px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00DC82]">
              MockTP Internal
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
              Beta Applications
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              {applications.length} total applications. Showing {filteredApplications.length} after filters.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadApplications}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={exportCsv}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={logout}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <XCircle size={16} />
              Sign out
            </button>
          </div>
        </header>

        <section className="grid gap-3 py-5 sm:grid-cols-2 lg:grid-cols-6">
          {STATUSES.map((status) => (
            <div key={status} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-xs text-slate-400">{status}</p>
              <p className="mt-1 text-2xl font-semibold">{summary.statusCounts[status] || 0}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-3 pb-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border border-[#00DC82]/25 bg-[#00DC82]/10 p-4">
            <p className="text-xs text-[#9EF5CB]">Total applications</p>
            <p className="mt-1 text-2xl font-semibold">{applications.length}</p>
          </div>
          {PLATFORMS.map((platform) => (
            <div key={platform.value} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-xs text-slate-400">{platform.label}</p>
              <p className="mt-1 text-2xl font-semibold">{summary.platformCounts[platform.value] || 0}</p>
            </div>
          ))}
        </section>

        <section className="mb-5 grid gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 lg:grid-cols-[2fr_1fr_1fr_1fr_auto]">
          <label className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={filters.search}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              placeholder="Search name, email, ADI number or driving school"
              className="h-10 w-full rounded-lg border border-white/10 bg-[#080C16] pl-9 pr-3 text-sm outline-none focus:border-[#00DC82]"
            />
          </label>
          <select
            value={filters.country}
            onChange={(event) => setFilters((current) => ({ ...current, country: event.target.value }))}
            className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
          >
            <option value="all">All countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
          <select
            value={filters.platform}
            onChange={(event) => setFilters((current) => ({ ...current, platform: event.target.value }))}
            className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
          >
            <option value="all">All platforms</option>
            {PLATFORMS.map((platform) => (
              <option key={platform.value} value={platform.value}>{platform.label}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <label className="flex h-10 items-center gap-2 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={filters.favouritesOnly}
              onChange={(event) =>
                setFilters((current) => ({ ...current, favouritesOnly: event.target.checked }))
              }
              className="accent-[#00DC82]"
            />
            Favourites
          </label>
        </section>

        <section className="space-y-3">
          {loading && <p className="text-sm text-slate-400">Loading applications...</p>}
          {!loading && filteredApplications.length === 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-sm text-slate-400">
              No applications match the current filters.
            </div>
          )}
          {filteredApplications.map((application) => (
            <article key={application.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() =>
                        updateApplication(
                          application.id,
                          { isFavourite: !application.isFavourite },
                          application.isFavourite ? "Removed from favourites." : "Marked as favourite."
                        )
                      }
                      className="mt-1 text-[#00DC82] disabled:opacity-50"
                      disabled={savingId === application.id}
                      aria-label="Toggle favourite"
                    >
                      <Star size={20} fill={application.isFavourite ? "currentColor" : "none"} />
                    </button>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-semibold">{application.name || "Unnamed applicant"}</h2>
                      <p className="truncate text-sm text-slate-400">{application.email}</p>
                      <p className="mt-2 text-xs text-slate-500">
                        Submitted {formatDateTime(application.created_at || application.createdAt)}
                      </p>
                    </div>
                  </div>

                  <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <dt className="text-xs text-slate-500">Country</dt>
                      <dd className="text-slate-200">{application.country || "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Platform</dt>
                      <dd className="text-slate-200">{platformLabel(application.preferredPlatform)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">ADI Number</dt>
                      <dd className="text-slate-200">{application.adi_number || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Driving School</dt>
                      <dd className="text-slate-200">{application.school || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Applicant Type</dt>
                      <dd className="text-slate-200">{application.applicantType || "Public Applicant"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Invitation Code</dt>
                      <dd className="text-slate-200">{application.invitationCode || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Invitation Code Valid</dt>
                      <dd className="text-slate-200">{application.invitationCodeValid ? "Yes" : "No"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Invitation Code Used</dt>
                      <dd className="text-slate-200">{application.invitationCodeUsed ? "Yes" : "No"}</dd>
                    </div>
                  </dl>
                </div>

                <div className="grid gap-3 xl:w-[420px]">
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                    <select
                      value={application.status || "New"}
                      onChange={(event) =>
                        updateApplication(application.id, { status: event.target.value }, "Status updated.")
                      }
                      className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                    <button
                      onClick={() =>
                        updateApplication(
                          application.id,
                          {
                            invitationSentAt: new Date().toISOString(),
                            invitationPlatform: invitationPlatformDraft[application.id] || null,
                            invitationEmail: invitationEmailDraft[application.id]?.trim() || null,
                          },
                          "Invitation marked as sent."
                        )
                      }
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#00DC82] px-3 text-sm font-semibold text-[#080C16] hover:bg-[#00B86B]"
                    >
                      <Send size={15} />
                      Invitation sent
                    </button>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Invitation details</label>
                    <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
                      <select
                        value={invitationPlatformDraft[application.id] || ""}
                        onChange={(event) =>
                          setInvitationPlatformDraft((current) => ({
                            ...current,
                            [application.id]: event.target.value,
                          }))
                        }
                        className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
                      >
                        <option value="">No platform</option>
                        {INVITATION_PLATFORMS.map((platform) => (
                          <option key={platform.value} value={platform.value}>{platform.label}</option>
                        ))}
                      </select>
                      <input
                        type="email"
                        value={invitationEmailDraft[application.id] || ""}
                        onChange={(event) =>
                          setInvitationEmailDraft((current) => ({
                            ...current,
                            [application.id]: event.target.value,
                          }))
                        }
                        placeholder="Invitation email"
                        className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
                      />
                      <button
                        onClick={() =>
                          updateApplication(
                            application.id,
                            {
                              invitationPlatform: invitationPlatformDraft[application.id] || null,
                              invitationEmail: invitationEmailDraft[application.id]?.trim() || null,
                            },
                            "Invitation details saved."
                          )
                        }
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-white/10 px-3 text-sm hover:bg-white/5"
                      >
                        <Save size={15} />
                        Save
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      Current: {invitationPlatformLabel(application.invitationPlatform)}
                      {application.invitationEmail ? ` · ${application.invitationEmail}` : ""}
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Invitation sent date</label>
                    <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
                      <input
                        type="datetime-local"
                        value={invitationDraft[application.id] || ""}
                        onChange={(event) =>
                          setInvitationDraft((current) => ({
                            ...current,
                            [application.id]: event.target.value,
                          }))
                        }
                        className="h-10 rounded-lg border border-white/10 bg-[#080C16] px-3 text-sm outline-none focus:border-[#00DC82]"
                      />
                      <button
                        onClick={() => {
                          const value = invitationDraft[application.id];
                          updateApplication(
                            application.id,
                            { invitationSentAt: value ? new Date(value).toISOString() : null },
                            value ? "Invitation date saved." : "Invitation date cleared."
                          );
                        }}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-white/10 px-3 text-sm hover:bg-white/5"
                      >
                        <Save size={15} />
                        Save
                      </button>
                      <button
                        onClick={() =>
                          updateApplication(application.id, { invitationSentAt: null }, "Invitation date cleared.")
                        }
                        className="h-10 rounded-lg border border-white/10 px-3 text-sm hover:bg-white/5"
                      >
                        Clear
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      Current: {formatDateTime(application.invitationSentAt)}
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Private admin notes</label>
                    <textarea
                      value={notesDraft[application.id] || ""}
                      onChange={(event) =>
                        setNotesDraft((current) => ({ ...current, [application.id]: event.target.value }))
                      }
                      rows={3}
                      className="w-full resize-y rounded-lg border border-white/10 bg-[#080C16] px-3 py-2 text-sm outline-none focus:border-[#00DC82]"
                      placeholder="Add internal notes for the MockTP team"
                    />
                    <button
                      onClick={() =>
                        updateApplication(
                          application.id,
                          { adminNotes: notesDraft[application.id] || "" },
                          "Notes saved."
                        )
                      }
                      className="mt-2 inline-flex h-9 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm hover:bg-white/5"
                    >
                      <Save size={15} />
                      Save notes
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
}
